// src/services/feedbackService.ts
import api from './api';
import {
  Feedback,
  FeedbackAdminUpdate,
  FeedbackStatistics,
  FeedbackListResponse,
  FeedbackSearchParams,
  FeedbackTypeOption,
  ReportType
} from '../types/feedback';

const FEEDBACK_ENDPOINTS = {
  list: '/feedbacks/',
  respond: (id: number) => `/feedbacks/${id}/admin`,
  statistics: '/feedbacks/statistics/overview',
  search: '/feedbacks/search/',
  detail: (id: number) => `/feedbacks/${id}`,
  delete: (id: number) => `/feedbacks/${id}`,
  types: '/feedbacks/types/available',
};

export const getFeedbacks = async (
  status?: 'pending' | 'resolved',
  skip: number = 0,
  limit: number = 50,
  reportType?: ReportType,
  userId?: number
): Promise<FeedbackListResponse> => {
  console.log('[FeedbackService] Fetching feedbacks:', { status, skip, limit, reportType, userId });
  const params: Record<string, any> = { skip, limit };
  if (status === 'pending') {
    params.is_resolved = false;
  } else if (status === 'resolved') {
    params.is_resolved = true;
  }
  if (reportType) {
    params.report_type = reportType;
  }
  if (userId) {
    params.user_id = userId;
  }
  const response = await api.get<FeedbackListResponse>(FEEDBACK_ENDPOINTS.list, { params });
  console.log('[FeedbackService] Feedbacks fetched:', response.data);
  return response.data;
};

export const searchFeedbacks = async (query: string, params?: FeedbackSearchParams): Promise<Feedback[]> => {
  console.log('[FeedbackService] Searching feedbacks:', { query, params });
  const searchParams: Record<string, any> = { q: query, ...params };
  const response = await api.get<Feedback[]>(FEEDBACK_ENDPOINTS.search, { params: searchParams });
  console.log('[FeedbackService] Feedbacks search result:', response.data);
  return response.data;
};

export const getFeedbackById = async (id: number): Promise<Feedback> => {
  console.log('[FeedbackService] Fetching feedback by id:', id);
  const response = await api.get<Feedback>(FEEDBACK_ENDPOINTS.detail(id));
  console.log('[FeedbackService] Feedback fetched:', response.data);
  return response.data;
};

export const respondToFeedback = async (id: number, payload: FeedbackAdminUpdate): Promise<Feedback> => {
  console.log('[FeedbackService] Responding to feedback:', id, payload);
  const response = await api.put<Feedback>(FEEDBACK_ENDPOINTS.respond(id), payload);
  console.log('[FeedbackService] Feedback responded:', response.data);
  return response.data;
};

export const deleteFeedback = async (id: number): Promise<void> => {
  console.log('[FeedbackService] Deleting feedback:', id);
  await api.delete(FEEDBACK_ENDPOINTS.delete(id));
  console.log('[FeedbackService] Feedback deleted:', id);
};

export const getFeedbackStatistics = async (): Promise<FeedbackStatistics> => {
  console.log('[FeedbackService] Fetching feedback statistics');
  const response = await api.get<FeedbackStatistics>(FEEDBACK_ENDPOINTS.statistics);
  console.log('[FeedbackService] Feedback statistics fetched:', response.data);
  return response.data;
};

export const getAvailableTypes = async (): Promise<FeedbackTypeOption[]> => {
  console.log('[FeedbackService] Fetching available feedback types');
  try {
    const response = await api.get<FeedbackTypeOption[]>(FEEDBACK_ENDPOINTS.types);
    console.log('[FeedbackService] Available types fetched:', response.data);
    return response.data;
  } catch (error) {
    // Fallback to default types if endpoint not available
    console.log('[FeedbackService] Using fallback types');
    return [
      { value: 'bug', label: 'Bug' },
      { value: 'feature', label: 'Feature' },
      { value: 'complaint', label: 'Complaint' },
      { value: 'general', label: 'General' },
      { value: 'other', label: 'Other' },
    ];
  }
};

export default {
  getFeedbacks,
  searchFeedbacks,
  getFeedbackById,
  respondToFeedback,
  deleteFeedback,
  getFeedbackStatistics,
  getAvailableTypes,
};
